import { Space } from '@amzn/finspace-service-typescript-client';
import { FinSpaceServiceApi } from '../api/finspaceServiceApi';
import { handleApiRequest } from '../api/utils/apiRequest';
import { createStore } from './store-factory';

interface GlobalState {
  spaces: Space[];
  selectedSpaceId: string | null;
  selectedSpace: Space | null;
  isLoading: boolean;
  isEnsuring: boolean; // Prevent concurrent ensureSpaceSelected calls
  error: any;
  fetchSpaces: () => Promise<void>;
  setSelectedSpaceId: (spaceId: string) => void;
  getSelectedSpaceId: () => string | null;
  ensureSpaceSelected: () => Promise<string>;
}

export const useGlobalStore = createStore<GlobalState>('global', (set, get) => ({
  spaces: [],
  selectedSpaceId: null,
  selectedSpace: null,
  isLoading: false,
  isEnsuring: false,
  error: null,

  fetchSpaces: async () => {
    set({ isLoading: true, error: null });
    try {
      const result = await handleApiRequest(() => FinSpaceServiceApi.getSpaces());
      const spaces = result?.spaces || [];
      const currentSelectedId = get().selectedSpaceId;
      const selectedSpaceId = currentSelectedId || spaces[0]?.id || null;
      const selectedSpace = spaces.find(space => space.id === selectedSpaceId) || null;
      
      set({ 
        spaces, 
        selectedSpaceId,
        selectedSpace,
        isLoading: false 
      });
    } catch (error) {
      set({ error, isLoading: false });
    }
  },

  setSelectedSpaceId: (spaceId: string) => {
    const { spaces } = get();
    const selectedSpace = spaces.find(space => space.id === spaceId) || null;
    set({ selectedSpaceId: spaceId, selectedSpace });
  },

  getSelectedSpaceId: () => {
    return get().selectedSpaceId;
  },

  ensureSpaceSelected: async () => {
    const { selectedSpaceId, spaces, fetchSpaces, isEnsuring } = get();
    
    // Prevent concurrent calls
    if (isEnsuring) {
      // Wait for ongoing operation to complete
      while (get().isEnsuring) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return get().selectedSpaceId || '';
    }
    
    if (!selectedSpaceId) {
      set({ isEnsuring: true });
      try {
        if (spaces.length === 0) {
          await fetchSpaces();
        }
        const currentSpaces = get().spaces;
        if (currentSpaces.length > 0 && currentSpaces[0].id) {
          const firstSpaceId = currentSpaces[0].id;
          get().setSelectedSpaceId(firstSpaceId);
          return firstSpaceId;
        }
        throw new Error('No spaces available');
      } finally {
        set({ isEnsuring: false });
      }
    }
    
    return selectedSpaceId;
  },
}));
